import { ExponentialBackoff, handleWhen, retry } from 'cockatiel';

import { AUDIO_LEVEL_ANALYSER_SETTINGS } from '@eluve/mic-utils';
import { sleep } from '@eluve/utils';

import type {
  MsgToMain,
  PcmCaptureStats,
  WorkletMsgFromMain,
  WorkletMsgToMain,
} from '../workers/messages-types';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore Import worklet URL - works in both Vite and Webpack
import audioWorkletUrl from '../workers/pcm-capture.worklet?worker&url';

import {
  AudioSource,
  AudioSourceConfig,
  AudioSourceInitResult,
} from './audio-source';
import {
  CaptureProgress,
  STARVED_QUANTA_ALERT_THRESHOLD,
  isCaptureShortfall,
  starvedFraction,
  toCaptureProgress,
} from './pcm-capture-stats';

const IDEAL_AUDIO_SAMPLE_RATE = 48_000;
const WORKLET_FLUSH_TIMEOUT = 5000;
const RESUME_STATECHANGE_TIMEOUT_MS = 500;

const addModuleRetry = retry(
  handleWhen(() => true),
  {
    maxAttempts: 3,
    backoff: new ExponentialBackoff({ initialDelay: 500 }),
  },
);

const resumeRetry = retry(
  handleWhen(() => true),
  {
    maxAttempts: 3,
    backoff: new ExponentialBackoff({ initialDelay: 100 }),
  },
);

/**
 * Wait until `audioContext.state === 'running'` or `timeoutMs` elapses.
 * Resolves in both cases — the caller checks state after the await and
 * decides whether to throw / retry. `resume()` is allowed to resolve before
 * the underlying state transition fires `statechange`, so a bounded wait
 * here closes the gap before we declare the context unable to start.
 */
async function waitForRunning(
  audioContext: AudioContext,
  timeoutMs: number,
): Promise<void> {
  if (audioContext.state === 'running') return;
  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      audioContext.removeEventListener('statechange', onChange);
      clearTimeout(timer);
      resolve();
    };
    const onChange = () => {
      if (audioContext.state === 'running') finish();
    };
    const timer = setTimeout(finish, timeoutMs);
    audioContext.addEventListener('statechange', onChange);
  });
}

/**
 * PCM Audio Source - Captures raw PCM audio using AudioWorklet
 * Encodes to Opus using MediaBunny in the worker
 */
export class PcmAudioSource extends AudioSource {
  private audioWorkletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private audioContextStateChangeHandler: (() => void) | null = null;
  // Latest cumulative counters stamped on worklet pcm messages. [ENG-5045]
  private latestWorkletStats: PcmCaptureStats | null = null;

  constructor(config: AudioSourceConfig) {
    super(config);
  }

  getMode(): 'pcm' {
    return 'pcm';
  }

  /**
   * Latest cumulative capture counters stamped on worklet pcm messages,
   * converted to seconds of captured audio. Null until the first stamped
   * block arrives. [ENG-5045]
   */
  override getCaptureProgress(): CaptureProgress | null {
    if (!this.latestWorkletStats || !this.audioContext) return null;
    return toCaptureProgress(
      this.latestWorkletStats,
      this.audioContext.sampleRate,
    );
  }

  async initialize(): Promise<AudioSourceInitResult> {
    this.logger('info', 'Initializing PCM audio source (AudioWorklet mode)');

    const trackSettings = this.mediaStream.getAudioTracks()[0]?.getSettings();
    this.logger('info', 'PCM audio source track settings', {
      sampleRate: trackSettings?.sampleRate,
      channelCount: trackSettings?.channelCount,
      deviceId: trackSettings?.deviceId,
    });

    // Create AudioContext with ideal sample rate for Opus
    const audioContext = new AudioContext({
      sampleRate: IDEAL_AUDIO_SAMPLE_RATE,
    });
    this.audioContext = audioContext;

    // Ensure AudioContext is running — Chrome's autoplay policy may start it
    // in "suspended" state, especially in extension offscreen documents where
    // there is no user gesture context. A suspended AudioContext means the
    // AudioWorklet's process() will never be called, resulting in zero audio.
    if (audioContext.state === 'suspended') {
      this.logger('warn', 'AudioContext is suspended, attempting to resume', {
        state: audioContext.state,
      });
      let resumeAttempts = 0;
      await resumeRetry.execute(async ({ attempt }) => {
        resumeAttempts = attempt + 1;
        if (attempt > 0) {
          this.logger('warn', 'Retrying AudioContext resume', {
            attempt: resumeAttempts,
            state: audioContext.state,
          });
        }
        await Promise.race([
          audioContext.resume(),
          waitForRunning(audioContext, RESUME_STATECHANGE_TIMEOUT_MS),
        ]);
        await waitForRunning(audioContext, RESUME_STATECHANGE_TIMEOUT_MS);

        if (audioContext.state !== 'running') {
          throw new Error(
            `AudioContext failed to start: state is "${audioContext.state}"`,
          );
        }
      });
      this.logger('info', 'AudioContext resumed successfully', {
        state: audioContext.state,
        attempts: resumeAttempts,
      });
    }

    if (audioContext.state !== 'running') {
      throw new Error(
        `AudioContext failed to start: state is "${audioContext.state}"`,
      );
    }

    // Auto-resume if Chrome suspends the context mid-recording (e.g., due to
    // memory pressure, tab backgrounding, or device changes). Track whether
    // we've seen a suspension so we can log the recovery transition — without
    // this, the success path is silent and "stuck suspended" is indistinguishable
    // from "recovered cleanly" in Datadog.
    let suspendedMidRecording = false;
    this.audioContextStateChangeHandler = () => {
      if (audioContext.state === 'suspended') {
        suspendedMidRecording = true;
        this.logger(
          'warn',
          'AudioContext suspended mid-recording, attempting resume',
          { state: audioContext.state },
        );
        audioContext.resume().catch((e) => {
          this.logger('error', 'Failed to resume suspended AudioContext', {
            error: e,
          });
        });
      } else if (audioContext.state === 'running' && suspendedMidRecording) {
        suspendedMidRecording = false;
        this.logger(
          'info',
          'AudioContext resumed after mid-recording suspension',
          { state: audioContext.state },
        );
      }
    };
    audioContext.addEventListener(
      'statechange',
      this.audioContextStateChangeHandler,
    );

    if (audioContext.sampleRate !== IDEAL_AUDIO_SAMPLE_RATE) {
      this.logger('warn', 'AudioContext sample rate mismatch', {
        requestedSampleRate: IDEAL_AUDIO_SAMPLE_RATE,
        actualSampleRate: audioContext.sampleRate,
        trackSampleRate: trackSettings?.sampleRate,
      });
    }

    // Load AudioWorklet module (with retry for transient network failures)
    await addModuleRetry.execute(async (context) => {
      if (context.attempt > 0) {
        this.logger('warn', 'Retrying AudioWorklet module load', {
          attempt: context.attempt + 1,
        });
      }
      await audioContext.audioWorklet.addModule(audioWorkletUrl);
    });

    // Create AudioWorkletNode
    this.audioWorkletNode = new AudioWorkletNode(
      this.audioContext,
      'pcm-capture',
      {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [this.channelCount],
        processorOptions: {
          channels: this.channelCount,
          blockFrames: 2048,
        },
      },
    );

    // Wait for worklet to signal it's ready before connecting audio source
    await new Promise<void>((resolve) => {
      const readyHandler = (e: MessageEvent<WorkletMsgToMain>) => {
        if (e.data.type === 'worklet-ready') {
          this.logger('info', 'AudioWorkletNode is ready');
          this.audioWorkletNode?.port.removeEventListener(
            'message',
            readyHandler,
          );
          resolve();
        }
      };
      this.audioWorkletNode?.port.addEventListener('message', readyHandler);
      this.audioWorkletNode?.port.start();
    });

    // Connect audio graph — store source node for explicit cleanup (WebKit leak)
    this.sourceNode = this.audioContext.createMediaStreamSource(
      this.mediaStream,
    );
    this.audioWorkletNode.connect(this.audioContext.destination);
    this.sourceNode.connect(this.audioWorkletNode);

    // Waveform visualization AnalyserNode (fan-out from the same source).
    const waveformAnalyser = audioContext.createAnalyser();
    waveformAnalyser.fftSize = AUDIO_LEVEL_ANALYSER_SETTINGS.fftSize;
    waveformAnalyser.smoothingTimeConstant =
      AUDIO_LEVEL_ANALYSER_SETTINGS.smoothingTimeConstant;
    waveformAnalyser.minDecibels = AUDIO_LEVEL_ANALYSER_SETTINGS.minDecibels;
    waveformAnalyser.maxDecibels = AUDIO_LEVEL_ANALYSER_SETTINGS.maxDecibels;
    this.sourceNode.connect(waveformAnalyser);
    this.waveformAnalyserNode = waveformAnalyser;

    // Set up mute detection (reuses existing AudioContext)
    this.setupMuteDetection(this.audioContext);

    // Attach track lifecycle listeners (ended + log-only mute/unmute)
    this.setupTrackEventListeners();

    // Forward PCM data from worklet to worker
    this.audioWorkletNode.port.onmessage = (
      ev: MessageEvent<WorkletMsgToMain>,
    ) => {
      const msg = ev.data;
      if (msg.type === 'pcm') {
        this.latestWorkletStats = msg.stats;
        const transfers: Transferable[] = [msg.left];
        if (msg.right) transfers.push(msg.right);
        this.postMessage(
          {
            type: 'pcm',
            channels: msg.channels,
            timestamp: msg.timestamp,
            left: msg.left,
            right: msg.right,
            seq: msg.seq,
            captureWallMs: msg.captureWallMs,
          },
          transfers,
        );
      }
    };

    // Listen for encoded audio from worker
    this.worker.addEventListener('message', this.workerMessageHandler);

    return {
      sampleRate: this.audioContext.sampleRate,
      channels: this.channelCount as 1 | 2,
    };
  }

  /**
   * Handle messages from the worker (encoded audio)
   */
  private workerMessageHandler = (e: MessageEvent<MsgToMain>): void => {
    const msg = e.data;
    if (msg.type === 'audio-available') {
      // Create blob from encoded audio
      const blob = new Blob([msg.data as BlobPart], {
        type: 'audio/webm; codecs=opus',
      });

      // Publish to subject for live transcription
      this.audioPacketSubject.next({
        timestamp: msg.timestamp,
        data: blob,
      });

      // Report bytes received to parent actor
      this.onBytesReceived(msg.data.byteLength);
    }
  };

  async stop(): Promise<void> {
    this.logger('info', 'Stopping PCM audio source');

    // Keep the `audio-available` → audioPacketSubject forwarder attached through
    // teardown. After this we flush the worklet and (in webWorkerAudio) send the
    // worker `stop`, whose `finalizeAll` emits the encoder's final-flush tail
    // (~300ms). Those packets must still reach the subject so the websocket can
    // drain them before STOP_RECORDING — removing this listener here (as before)
    // dropped the tail and truncated the websocket file vs. the backup, breaking
    // the hash-dedup. The listener is cleaned up when webWorkerAudio terminates
    // the worker. [ENG-4721]

    // Give a buffer for in-flight audio to drain through the pipeline
    await sleep(150);

    // Wait for worklet to complete flushing
    let flushCompleted = false;
    const workletFlushed = new Promise<void>((resolve) => {
      const flushCompleteHandler = (e: MessageEvent<WorkletMsgToMain>) => {
        if (e.data.type === 'flush-complete') {
          const { stats } = e.data;
          const captureStats = {
            emptyQuanta: stats.emptyQuanta,
            totalQuanta: stats.totalQuanta,
            starvedFraction: starvedFraction(stats),
          };
          this.logger('info', 'PCM capture stats', {
            ...captureStats,
            gapCount: stats.gapCount,
            sampleRate: this.audioContext?.sampleRate,
          });
          if (isCaptureShortfall(stats)) {
            this.logger('error', 'Recording capture shortfall', {
              ...captureStats,
              threshold: STARVED_QUANTA_ALERT_THRESHOLD,
            });
          }
          this.logger('info', 'Worklet flush completed');
          this.audioWorkletNode?.port.removeEventListener(
            'message',
            flushCompleteHandler,
          );
          flushCompleted = true;
          resolve();
        }
      };
      this.audioWorkletNode?.port.addEventListener(
        'message',
        flushCompleteHandler,
      );
    });

    try {
      this.audioWorkletNode?.port.postMessage({
        type: 'flush',
      } satisfies WorkletMsgFromMain);

      await Promise.race([
        workletFlushed,
        sleep(WORKLET_FLUSH_TIMEOUT).then(() => {
          if (!flushCompleted) {
            this.logger('warn', 'Worklet flush timeout exceeded');
          }
        }),
      ]);
    } catch (e) {
      this.logger('error', 'Error flushing worklet', {
        error: e,
      });
    }

    // Tear down the audio graph
    try {
      this.audioWorkletNode?.disconnect();
    } catch (e) {
      this.logger('error', 'Error disconnecting AudioWorkletNode', {
        error: e,
      });
    }

    this.audioWorkletNode = null;

    // Explicitly disconnect the source node to prevent WebKit memory leak
    // (WebAudio/web-audio-api#2484) where MediaStreamAudioSourceNode persists
    // even after AudioContext.close()
    try {
      this.sourceNode?.disconnect();
    } catch (e) {
      this.logger('error', 'Error disconnecting source node', {
        error: e,
      });
    }

    this.sourceNode = null;

    // Remove statechange listener before closing
    if (this.audioContextStateChangeHandler) {
      this.audioContext?.removeEventListener(
        'statechange',
        this.audioContextStateChangeHandler,
      );
      this.audioContextStateChangeHandler = null;
    }

    // Clean up waveform analyser (closing AudioContext disconnects all nodes)
    this.waveformAnalyserNode = null;

    // Close AudioContext
    try {
      await this.audioContext?.close();
    } catch (e) {
      this.logger('error', 'Error closing AudioContext', {
        error: e,
      });
    }

    this.audioContext = null;

    // Clean up mute detection
    await this.cleanupMuteDetection();

    // Detach track lifecycle listeners
    this.teardownTrackEventListeners();

    this.logger('info', 'PCM audio source stopped');
  }
}
